import { query, repository } from '@trlogic/client-js';
import { InstanceContainer } from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import {
  SandboxHistoryJsonModel,
  TransactionJsonModel,
  TransactionRequestType,
  TransactionVariableJsonModel,
} from '../domain/stream/transaction/TransactionDomains';
import CollectionNames from '../constant/CollectionNames';
import TransactionClient from '../client/transaction/TransactionClient';
import ComponentName from '../constant/ComponentName';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import FolderClient from '../client/folder/FolderClient';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import { ComponentDependency } from '../domain/ui/ComponentMetadata';
import { changeEvent, changeEventDirty } from '../redux/actions/ChannelActions';
import { ActionType } from '../constant/Constants';
import { ChannelUndeployDependent } from '../domain/stream/transaction/ChannelUndeployDependent';
import DeployErrorUtil from '../util/DeployErrorUtil';
import Query = query.Query;

const { eq } = query;

class TransactionService {
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private transactionClient: TransactionClient = GatewayContainer.getInstance().get(GatewayNames.TRANSACTION_CLIENT);
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);

  private readonly NAME_BLACKLIST = ['transaction', 'create', 'transaction-result-store', 'transaction-result-query', 'store-result', 'query-result'];

  public saveEvent = async (event: TransactionJsonModel, isUpdate: boolean, folder: EnrichedComponentCategory) => {
    try {
      if (event == undefined) return false;
      if (event.name == undefined || event.name == '') {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'messages.error.withoutName', defaultMessage: 'You can not save without name' }),
        });
        return false;
      }
      const isInBlacklist = this.NAME_BLACKLIST.includes(event.name);
      if (isInBlacklist) {
        MessageUtil.error({
          summary: <div>You cannot use this name</div>,
        });
        return false;
      }
      if (!isUpdate) {
        const isExist = await this.transactionClient.isExist(event.name);
        if (isExist) {
          MessageUtil.alreadyExist(LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }));
          return false;
        }
      }
      await this.transactionClient.save(event);
      if (!isUpdate && folder) {
        await this.folderClient.addItemToCategory(ComponentName.TRANSACTION, folder.id, event.name);
      }
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }),
        detail: (
          <div data-testid='channel_save_message'>
            <b>{event.name}</b> {LanguageUtil.get({ id: 'messages.saved', defaultMessage: 'saved.' })}
          </div>
        ),
      });
      dispatch(changeEventDirty(false));
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }),
        detail: (
          <div>
            <b>{event.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'not saved' })}
          </div>
        ),
      });
      return false;
    }
  };

  public removeEvent = async (event: TransactionJsonModel): Promise<boolean> => {
    try {
      const eventFilter: query.Filter = eq('name', event.name);
      const eventQuery: query.SelectQuery = query.QueryBuilder.select().where(eventFilter).from(CollectionNames.TRANSACTIONS).build();
      const status: boolean | undefined = (await this.repository.find<TransactionJsonModel>(eventQuery))?.status;
      if (event.name && status != undefined && !status) {
        const response = await this.transactionClient.remove(event.name);
        if (response.code) {
          return false;
        }
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }),
          detail: (
            <div>
              <b>{event.name}</b> {LanguageUtil.get({ id: 'messages.remove', defaultMessage: 'removed' })}
            </div>
          ),
        });
        return true;
      }
      return false;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      console.log(dependencies);
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.TRANSACTION));
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }),
        detail: (
          <div>
            <b>{event.name}</b>
            {LanguageUtil.get({ id: 'messages.cannotremove', defaultMessage: 'can not remove' })}
          </div>
        ),
      });
      return false;
    }
  };

  public deployEvent = async (event: TransactionJsonModel): Promise<void> => {
    if (event && event.name && event.name.trim().length > 0) {
      try {
        await this.transactionClient.deploy(event.name);
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }),
          detail: (
            <div data-testid='channel_deploy_message'>
              <b>{event.name}</b> {LanguageUtil.get({ id: 'messages.deploy', defaultMessage: 'deployed' })}
            </div>
          ),
        });
      } catch (e: any) {
        if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }))) {
          return;
        }
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }),
          detail: (
            <div>
              <b>{event.name}</b> {LanguageUtil.get({ id: 'messages.cannotdeployed', defaultMessage: 'can not deployed' })}
            </div>
          ),
        });
      }
    } else {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.deployWithoutName' }),
      });
    }
  };

  public listUndeployDependents = (id: string): Promise<ChannelUndeployDependent[]> => {
    return this.transactionClient.listUndeployDependents(id);
  };

  public unDeployEvent = async (event: TransactionJsonModel): Promise<void> => {
    if (event.name) {
      try {
        await this.transactionClient.undeploy(event.name);
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }),
          detail: (
            <div>
              <b>{event.name}</b> {LanguageUtil.get({ id: 'messages.undeployed', defaultMessage: 'undeployed' })}
            </div>
          ),
        });
      } catch (e: any) {
        if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }))) {
          return;
        }
        const dependencies: any = e.response.data;
        if (dependencies?.message == 'Dependency Exception') {
          dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.TRANSACTION));
        }
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' }),
          detail: (
            <div>
              <b>{event.name}</b> {LanguageUtil.get({ id: 'messages.cannotUndeployed', defaultMessage: 'can not undeployed' })}
            </div>
          ),
        });
      }
    }
  };

  public async bulkAction(ids: string[], type: ActionType): Promise<any[]> {
    const logs: any = await this.transactionClient.bulkAction(ids, type);
    return logs;
  }

  public removeEventVariable = async (event: TransactionJsonModel, eventVariable: TransactionVariableJsonModel, dependencies: ComponentDependency[]) => {
    if (eventVariable == undefined) {
      return MessageUtil.error({
        summary: 'Event Variable',
        detail: <div>{LanguageUtil.get({ id: 'messages.variable', defaultMessage: 'Variable undefined.' })}</div>,
      });
    }
    if (event.stage != 'DRAFT') {
      return MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.eventVariable', defaultMessage: 'Event Variable' }),
        detail: (
          <div>
            {LanguageUtil.get({
              id: 'messages.cannotRemoveVariableWhenDeployed',
              defaultMessage: 'You cannot remove variables when the channel is active. Please undeploy the channel first.',
            })}
          </div>
        ),
      });
    }
    const dependency: ComponentDependency[] = dependencies.filter((d) => d.dependentId == eventVariable.name);
    if (dependency.length > 0) {
      dispatch(openBulkActionDialogFromDependency({ id: event.name, reason: 'DELETE', dependencies: dependency, status: false }, ComponentName.TRANSACTION));
      return;
    }
    const index = event.variables.indexOf(eventVariable);
    if (index != -1) {
      event.variables.splice(index, 1);
      dispatch(changeEvent(event));
      MessageUtil.success({
        summary: 'Event Variable',
        detail: <div>{LanguageUtil.get({ id: 'messages.removed', defaultMessage: 'removed' })}</div>,
      });
    }
  };

  public getEventCount(query: Query): Promise<number> {
    return this.transactionClient.getCount(query);
  }

  public async sendEvent(
    selectedTransaction: TransactionJsonModel | undefined,
    test: boolean,
    request: string | undefined,
    key: string | undefined,
    method: TransactionRequestType | undefined,
    id: string | undefined,
  ) {
    let payload;
    try {
      if (method == undefined) return;
      if (method == 'SUBMIT' || method == 'STORE') {
        if (request == undefined) {
          MessageUtil.error({
            summary: 'Sandbox',
            detail: <div>Request cannot be empty!</div>,
          });
          return;
        }
        try {
          payload = JSON.parse(request);
        } catch (e) {
          MessageUtil.error({
            summary: 'Sandbox',
            detail: <div>Request is not a valid JSON!</div>,
          });
          return;
        }
        if (selectedTransaction == undefined || Object.keys(payload).length == 0) {
          MessageUtil.error({
            summary: 'Sandbox',
            detail: <div>Transaction cannot be empty!</div>,
          });
          return;
        }
        if (key == undefined || key.trim().length == 0) {
          MessageUtil.error({
            summary: 'Sandbox',
            detail: <div>Key field cannot be empty!</div>,
          });
          return;
        }
        return this.transactionClient.sendEvent(method, selectedTransaction.name, payload, key, id, test);
      } else if (method == 'UPDATE' || method == 'RESUBMIT') {
        if (id == undefined || request == undefined) {
          MessageUtil.error({
            summary: 'Sandbox',
            detail: <div>Request and id cannot be empty!</div>,
          });
          return;
        }
        try {
          payload = JSON.parse(request);
          return this.transactionClient.sendEvent(method, '', payload, '', id, test);
        } catch (e) {
          return;
        }
      } else if (method == 'DELETE') {
        if (id == undefined) {
          MessageUtil.error({
            summary: 'Sandbox',
            detail: <div>Id cannot be empty!</div>,
          });
          return;
        }
        return this.transactionClient.sendEvent(method, '', {}, '', id, test);
      }
    } catch (e) {
      console.log(e);
    }
  }

  public getHistory = async (date: string): Promise<SandboxHistoryJsonModel> => {
    return await this.transactionClient.getHistory(date);
  };

  public pinHistory = async (id: string): Promise<void> => {
    await this.transactionClient.pinHistory(id);
  };

  public deleteHistory = async (id: string): Promise<void> => {
    await this.transactionClient.deleteHistory(id);
  };
}

export default TransactionService;
